/**
 * Collection card colour swatches + lowest available price.
 *
 * Enriches Graphene-rendered product cards (collection + search grids) after
 * page load. Graphene emits `data-product-handle` on each card; this module
 * eagerly fetches the per-product fragment via the Section Rendering API
 * (throttled by a concurrency queue), injects the colour-swatch row (wiring the
 * hover image-swap), and replaces Graphene's rendered price with the lowest
 * available child price computed in the fragment (combined listings hide
 * per-child prices from Graphene's feed).
 *
 * Exposes `window.SwyftCardSwatches.enrich(container)`, called by
 * MainCollectionComponent on initial load and every Graphene re-render.
 */
(function () {
  const SECTION_ID = 'collection-card-swatches';
  // Six at once hit Shopify's storefront rate limit on busy collections (429s in Bloodhound, Sept 2026).
  const MAX_CONCURRENT = 4;
  // A 429 or 503 is retried after the wait Shopify asks for (or 1s, 2s, 4s) instead of leaving a stale price.
  const RETRY_STATUSES = [429, 503];
  const MAX_RETRIES = 3;

  /** @type {Map<string, Promise<string>>} cache of fragment HTML keyed by handle */
  const fragmentCache = new Map();
  let inFlight = 0;
  /** @type {Array<() => void>} pending fetch starters */
  const queue = [];

  /** Pump the queue while under the concurrency cap. */
  function pump() {
    while (inFlight < MAX_CONCURRENT && queue.length) {
      const start = queue.shift();
      start();
    }
  }

  /**
   * How long to wait before retrying: Shopify's Retry-After (seconds) when it sends one, else 1s, 2s, 4s.
   * @param {Response} response
   * @param {number} attempt
   */
  function retryDelay(response, attempt) {
    const header = Number(response.headers.get('Retry-After'));
    return Number.isFinite(header) && header > 0 ? Math.min(header, 10) * 1000 : 1000 * 2 ** attempt;
  }

  /** @param {string} handle */
  function fetchFragment(handle) {
    if (fragmentCache.has(handle)) return fragmentCache.get(handle);

    const promise = new Promise((resolve, reject) => {
      const attemptFetch = (attempt) => {
        queue.push(() => {
          inFlight += 1;
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const url = `${root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?section_id=${SECTION_ID}`;
          fetch(url)
            .then((r) => {
              if (r.ok) return r.text();
              if (RETRY_STATUSES.includes(r.status) && attempt < MAX_RETRIES) {
                // The slot is freed while waiting, so other cards keep loading.
                setTimeout(() => attemptFetch(attempt + 1), retryDelay(r, attempt));
                return null;
              }
              return Promise.reject(new Error(`HTTP ${r.status}`));
            })
            .then((html) => {
              if (html !== null) resolve(html);
            })
            .catch((err) => {
              // Evict so a later re-render can retry rather than replaying the rejection.
              fragmentCache.delete(handle);
              reject(err);
            })
            .finally(() => {
              inFlight -= 1;
              pump();
            });
        });
        pump();
      };
      attemptFetch(0);
    });

    fragmentCache.set(handle, promise);
    return promise;
  }

  /**
   * Wire hover image-swap for the pills inside one card. Scoped per card and
   * idempotent so lazy/staggered injection never double-binds.
   * @param {HTMLElement} card
   */
  function wireHover(card) {
    if (card.hasAttribute('data-swatch-hover-wired')) return;
    const image = card.querySelector('.card__media .media img');
    const pills = card.querySelector('.variant__pills');
    if (!image || !pills) return;

    if (!image.hasAttribute('data-original-srcset')) {
      image.setAttribute('data-original-srcset', image.srcset);
    }
    if (!image.hasAttribute('data-original-src')) {
      image.setAttribute('data-original-src', image.src);
    }

    pills.querySelectorAll('.variant__pill').forEach((pill) => {
      const span = pill.querySelector('span');
      if (!span) return;
      pill.addEventListener('mouseenter', () => {
        const newSrcSet = span.getAttribute('data-srcset');
        const newSrc = span.getAttribute('data-src');
        if (!newSrcSet || !newSrc) return;
        // Skip if already showing this colour (the browser cache serves repeats).
        if (image.getAttribute('src') === newSrc) return;
        image.srcset = newSrcSet;
        image.src = newSrc;
      });
    });

    pills.addEventListener('mouseleave', () => {
      const originalSrcSet = image.getAttribute('data-original-srcset');
      const originalSrc = image.getAttribute('data-original-src');
      if (!originalSrc || image.getAttribute('src') === originalSrc) return;
      image.srcset = originalSrcSet;
      image.src = originalSrc;
    });

    card.setAttribute('data-swatch-hover-wired', '');
  }

  /**
   * The `li` carrying data-is-rolled-up for a card (the card element itself or an
   * ancestor). Null when the card isn't inside one.
   * @param {HTMLElement} card
   * @returns {HTMLElement|null}
   */
  function rolledUpLi(card) {
    const li = card.matches('li') ? card : card.closest('li');
    return li && li.getAttribute('data-is-rolled-up') === 'true' ? li : null;
  }

  /**
   * Reveal a rolled-up card's price. CSS hides Graphene's stale price on
   * rolled-up cards until this flag is set, so the correct price (or, on failure,
   * Graphene's own) never flashes. No-op on non-rolled-up cards.
   * @param {HTMLElement} card
   */
  function markPriceReady(card) {
    const li = rolledUpLi(card);
    if (li) li.setAttribute('data-price-ready', '');
  }

  /**
   * Swap Graphene's rendered price for the lowest available child price from the
   * fetched fragment. Only rolled-up cards (combined listings, `li` with
   * data-is-rolled-up="true") get this — a standalone product's own Graphene
   * price is already correct. Replaces the card's existing price node in place;
   * if the card has none, injects into `.card-information` (or `.card__content`)
   * so it works whether or not Graphene still renders a price. Always reveals the
   * price afterwards so the loading skeleton never sticks.
   * @param {HTMLElement} card
   * @param {Document} doc parsed fragment
   */
  function replacePrice(card, doc) {
    const li = rolledUpLi(card);
    if (!li) return;

    const priceWrap = doc.querySelector('[data-swyft-card-price]');
    const newPrice = priceWrap && priceWrap.querySelector('.price');
    if (newPrice) {
      const existing = card.querySelector('.card-information .price') || card.querySelector('.price');
      if (existing) {
        existing.replaceWith(newPrice);
      } else {
        const target = card.querySelector('.card-information') || card.querySelector('.card__content');
        if (target) target.appendChild(newPrice);
      }
    }

    li.setAttribute('data-price-ready', '');
  }

  /** The delivery texts theme.liquid puts on the page, read once. */
  let deliveryTexts;
  function texts() {
    if (deliveryTexts === undefined) {
      try { deliveryTexts = JSON.parse(document.querySelector('[data-delivery-messages]')?.textContent || '{}'); } catch { deliveryTexts = {}; }
    }
    return deliveryTexts;
  }

  /**
   * The delivery line for a model card (every colour, no stock of its own), or a colour card Graphene sent no stock for: the usual next
   * day message when every colour is next day, the "some colours" one when only some are, else the Christmas message
   * when an in-stock colour arrives before the last date (marketing lead, 6 Oct 2026).
   * @param {HTMLElement} card
   * @param {Document} doc parsed fragment
   */
  function setModelDelivery(card, doc) {
    const data = doc.querySelector('[data-swyft-card-delivery]');
    const line = card.querySelector('.stock-check');
    // A colour card whose stock Graphene did send is left to main-collection-component.js; some collections (New In) send none.
    if (!data || !line || (!rolledUpLi(card) && line.dataset.stockCheck)) return;
    const dates = rolledUpLi(card)
      ? (data.dataset.dates || '').split(',').filter(Boolean)
      : (parseInt(data.dataset.currentStock, 10) > 0 && data.dataset.currentDate ? [data.dataset.currentDate] : []);
    const total = rolledUpLi(card) ? parseInt(data.dataset.total, 10) || 0 : 1;
    const today = new Date().toLocaleDateString('en-CA');
    const t = texts();
    const nextDay = dates.filter((d) => d <= today).length;
    let message = '';
    if (nextDay && nextDay === total) message = t.next_day_available || 'Available next day';
    else if (nextDay) message = t.next_day_some_colours || 'Next day in some colours';
    else if (t.christmas?.text && t.christmas.last_date && dates.some((d) => d < t.christmas.last_date)) message = t.christmas.text;
    line.dataset.modelMessage = message;
    line.style.display = message ? '' : 'none';
    line.innerHTML = message;
    line.classList.toggle('stock-check--christmas', Boolean(message) && message === t.christmas?.text);
    if (message) line.setAttribute('data-delivery-message', message);
  }

  /**
   * Fetch + inject the swatch row for a single card, then wire hover.
   * @param {HTMLElement} card
   */
  function enrichCard(card) {
    const handle = card.getAttribute('data-product-handle');
    if (!handle || card.hasAttribute('data-swatches-done')) return;
    if (card.querySelector('.variant__pills')) {
      card.setAttribute('data-swatches-done', '');
      markPriceReady(card);
      return;
    }
    card.setAttribute('data-swatches-done', '');

    fetchFragment(handle)
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Lowest available child price — replace Graphene's rendered price in place.
        replacePrice(card, doc);
        setModelDelivery(card, doc);

        const pills = doc.querySelector('.variant__pills');
        const hasPills = pills && pills.querySelector('.variant__pill');
        // A single-finish product's one swatch offers no choice; its finish name takes its place.
        const finish = doc.querySelector('[data-swyft-card-finish]');
        if (!hasPills && !finish) return;

        const content = card.querySelector('.card__content');
        const footer = card.querySelector('.card__footer');
        const anchor = content || card;
        const nodes = finish ? [finish] : [pills];
        const more = !finish && doc.querySelector('.more_colours');
        if (more) nodes.push(more);

        nodes.forEach((node) => {
          if (footer && footer.parentNode === content) {
            content.insertBefore(node, footer);
          } else {
            anchor.appendChild(node);
          }
        });

        wireHover(card);
      })
      .catch(() => {
        /* Card stays fully usable without swatches; allow a later re-render to retry. */
        card.removeAttribute('data-swatches-done');
        /* Reveal Graphene's own price rather than leaving the skeleton stuck. */
        markPriceReady(card);
      });
  }

  /**
   * Enrich every eligible card within a container. Idempotent across re-renders.
   * Fetches are kicked off eagerly for all cards (a page holds ~20) and throttled
   * by the concurrency queue, so prices are ready before the shopper scrolls to
   * them rather than fetched on-demand as each card enters the viewport.
   * @param {ParentNode} [container=document]
   */
  function enrich(container) {
    const scope = container || document;
    // The handle may sit on the `.grid__item` or the `.product-card-wrapper`.
    const cards = scope.querySelectorAll('[data-product-handle]:not([data-swatches-done])');
    cards.forEach((card) => {
      if (!card.getAttribute('data-product-handle')) return;
      if (!card.querySelector('.card__content')) return;
      enrichCard(card);
    });
  }

  window.SwyftCardSwatches = { enrich };
})();
